import Image from "next/image";
import Link from "next/link";
import { Calendar } from "lucide-react";
import { useLocale } from "next-intl";
import { format, parseISO } from "date-fns";
import { enUS, es } from "date-fns/locale";
import type { Locale } from "date-fns";
import { cn } from "@/lib/utils";
import type { BlogPostSummary } from "@/types/blog";
import type { LocaleCode } from "@/i18n/locales";

const DATE_FNS_LOCALES: Record<LocaleCode, Locale> = {
  "en-US": enUS,
  "es-US": es,
};

interface BlogPostCardProps {
  post: BlogPostSummary;
  className?: string;
}

export function BlogPostCard({ post, className }: BlogPostCardProps) {
  const locale = useLocale() as LocaleCode;
  const formatDate = (dateString: string) =>
    format(parseISO(dateString), "MMM d, yyyy", { locale: DATE_FNS_LOCALES[locale] });

  return (
    <article className={cn("group cursor-pointer", className)}>
      <Link href={`/about/blog/${post.slug}`}>
        <div className="space-y-4">
          {/* Image */}
          <div className="relative rounded-2xl overflow-hidden aspect-[16/10] bg-zinc-800">
            <Image
              src={post.image || "/placeholder.svg"}
              alt={post.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-500"
              style={post.imagePosition ? { objectPosition: post.imagePosition } : undefined}
            />
          </div>

          {/* Content */}
          <div className="space-y-3">
            {/* Author and Date */}
            <div className="flex items-center gap-2 text-sm text-yellow-500">
              <span className="font-medium">{post.author.name}</span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(post.publishedAt)}</span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-white group-hover:text-yellow-500 transition-colors line-clamp-2">
              {post.title}
            </h3>

            {/* Excerpt */}
            <p className="text-gray-300 text-sm leading-relaxed line-clamp-3">
              {post.excerpt}
            </p>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {post.categories.slice(0, 2).map((category) => (
                <span
                  key={category}
                  className="px-3 py-1 bg-zinc-800 text-white text-xs rounded-full hover:bg-zinc-700 transition-colors"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}
